// Axios instance wired to the auth store: attaches the bearer token, and on a
// 401 transparently refreshes once (single-flight) via the httpOnly cookie,
// then replays the original request.
import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import type { ApiResponse } from '../types.js';
import { useAuthStore } from './authStore.js';

export interface ApiClientOptions {
  baseURL: string;
}

type RetryableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

let refreshPromise: Promise<string | null> | null = null;

function refreshAccessToken(baseURL: string): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = axios
      .post<ApiResponse<{ accessToken: string }>>(`${baseURL}/api/auth/refresh`, {}, { withCredentials: true })
      .then((res) => {
        if (res.data.success) {
          useAuthStore.getState().setToken(res.data.data.accessToken);
          return res.data.data.accessToken;
        }
        useAuthStore.getState().clear();
        return null;
      })
      .catch(() => {
        useAuthStore.getState().clear();
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

export function createApiClient({ baseURL }: ApiClientOptions): AxiosInstance {
  const client = axios.create({ baseURL, withCredentials: true });

  client.interceptors.request.use((cfg) => {
    const token = useAuthStore.getState().accessToken;
    if (token) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
  });

  client.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
      const original = error.config as RetryableConfig | undefined;
      // Only the token endpoints are exempt from the refresh-retry (refreshing
      // on them is pointless or loops); /api/auth/me must still trigger a refresh.
      const url = original?.url ?? '';
      const noRefresh = /\/api\/auth\/(refresh|login|signup|logout)/.test(url);
      if (error.response?.status === 401 && original && !original._retried && !noRefresh) {
        original._retried = true;
        const token = await refreshAccessToken(baseURL);
        if (token) {
          original.headers.Authorization = `Bearer ${token}`;
          return client(original);
        }
      }
      // Surface the API's human-readable error (envelope `error` field) instead
      // of axios's generic "Request failed with status code NNN".
      const data = error.response?.data as { error?: unknown } | undefined;
      if (data && typeof data.error === 'string') error.message = data.error;
      return Promise.reject(error);
    },
  );

  return client;
}
